import React, { useCallback } from 'react';
import { StyleSheet, View, type ViewProps, type ViewStyle } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';
import { BodyText, Collapsible, Icon } from '@garnish/core';

export const LoyaltyHomeProgressBenefit = (props: LoyaltyHomeProgressBenefitProps) => {
  const { title, subtitle, benefitsCount, palette, children, onBenefitsOpened } = props;

  const minHeight = useStyle(
    () => ({
      minHeight: theme.spacing['15'] * benefitsCount,
    }),
    [benefitsCount],
  );

  const handleOnChange = useCallback(
    (expanded: boolean) => {
      if (expanded) onBenefitsOpened();
    },
    [onBenefitsOpened],
  );

  return (
    <Collapsible onChange={handleOnChange}>
      <Collapsible.Summary.Container
        style={[styles.summaryContainer, summaryPalettes[palette]]}
        expandedStyle={styles.summaryContainerExpanded}
      >
        <View style={styles.summaryContent}>
          <Icon name="IconSgRewardWhiteOutline" width={24} height={24} />

          <View>
            <BodyText style={styles.benefitLabel} sizeMatch={['14']} bold>
              {title}
            </BodyText>

            <BodyText style={styles.benefitLabel} sizeMatch={['14']}>
              {subtitle}
            </BodyText>
          </View>
        </View>

        <Collapsible.Icon
          iconName="IconCaretDown"
          iconColor={theme.colors.CREAM}
          iconStrokeWidth={1.5}
          style={styles.downCaret}
        />
      </Collapsible.Summary.Container>

      <Collapsible.Details style={minHeight}>{children}</Collapsible.Details>
    </Collapsible>
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  summaryContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: theme.radius.medium,
    padding: theme.spacing['4'],
    marginTop: theme.spacing['4'],
    marginHorizontal: theme.spacing['6'],
  },
  summaryDark: {
    backgroundColor: theme.colors.DARK_KALE,
    borderColor: theme.colors.CREAM,
  },
  summaryLight: {
    backgroundColor: theme.colors.BASIL,
    borderColor: theme.colors.KALE,
  },
  summaryContainerExpanded: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  summaryContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
  },
  benefitLabel: {
    color: theme.colors.CREAM,
  },
  progressBenefitSM: {
    color: theme.colors.KALE,
    textAlign: 'center',
    marginBottom: theme.spacing['16'],
  },
  downCaret: {
    alignSelf: 'center',
  },
});

// ─── Palettes ───────────────────────────────────────────────────────────────

const summaryPalettes: Record<LoyaltyHomeProgressBenefitProps['palette'], ViewStyle> = {
  dark: styles.summaryDark,
  light: styles.summaryLight,
};

// ─── Types ──────────────────────────────────────────────────────────────────

type LoyaltyHomeProgressBenefitProps = ViewProps & {
  title: string;
  subtitle: string;
  benefitsCount: number;
  palette: 'dark' | 'light';
  onBenefitsOpened: () => void;
};
